import { type ColumnDef, type Table } from "@tanstack/react-table";

import { type ColorVariant } from "../Chip/types";
import { type ActiveTabState } from "./ActiveTabState";
import { type AggregationFnOption, type BuiltinAggregationFnLabels } from "./grouping-aggregations";
import { type ColumnType } from "./validation/parseColumnType";

export type QueryFn = (...props: any[]) => unknown;
export type MakeQueryFn = (data: unknown) => QueryFn;

/**
 * Specifies the behavior and appearance of a column in the DataGrid, including how the column is
 * rendered, grouped, sorted, filtered, and aggregated.
 *
 * It lightly wraps the `ColumnDef` type from `@tanstack/react-table`, with a few important
 * differences:
 *
 *   - `aggregationFn` is replaced with `defaultAggregationFn` because we want users to be able to
 *     select the aggregation function in the DataGrid UI.
 *   - `fieldType` allows canvas authors to give the DataGrid a hint about what type the column has,
 *     which is useful for sorting, filtering, aggregation, and rendering.
 */
export type DataGridColumnDefBase<T, TData> =
	// `aggregationFn` is replaced with `defaultAggregationFn` because we want users to be able to
	// select the aggregation function in the DataGrid UI.
	ColumnDef<T> & {
		header?: string;
		copyable?: boolean;
		aggregationFnId?: string;
		defaultAggregationFn?: AggregationFnOption<TData> | BuiltinAggregationFnLabels;
		readonly?: boolean;
	};

export type DataGridColumnDefTag<T, TData> = DataGridColumnDefBase<T, TData> & {
	fieldType: "Tag";
	fieldTypeInfo?: {
		tags: { label: string; color: ColorVariant }[];
		defaultValue?: string[];
	};
	copyable?: boolean;
	defaultAggregationFn?: AggregationFnOption<TData> | BuiltinAggregationFnLabels;
	readonly?: boolean;
};

export const personDefaultValues = ["Empty", "Current user"] as const;
export type PersonDefaultValues = (typeof personDefaultValues)[number];

export type DataGridColumnDefPerson<T, TData> = DataGridColumnDefBase<T, TData> & {
	fieldType: "Person";
	fieldTypeInfo?: {
		defaultValue?: PersonDefaultValues;
	};
	copyable?: boolean;
	defaultAggregationFn?: AggregationFnOption<TData> | BuiltinAggregationFnLabels;
	readonly?: boolean;
};

export type DataGridColumnDefURL<T, TData> = DataGridColumnDefBase<T, TData> & {
	fieldType: "URL";
	fieldTypeInfo?: {
		textAccessorKey?: string;
	};
	copyable?: boolean;
	defaultAggregationFn?: AggregationFnOption<TData> | BuiltinAggregationFnLabels;
	readonly?: boolean;
};

export type DataGridColumnDef<T, TData> = DataGridColumnDefBase<T, TData> &
	(
		| { fieldType: "Auto"; fieldTypeInfo?: undefined }
		| { fieldType: "Boolean"; fieldTypeInfo?: undefined }
		| { fieldType: "Currency"; fieldTypeInfo?: undefined }
		| { fieldType: "Date time"; fieldTypeInfo?: undefined }
		| { fieldType: "Date"; fieldTypeInfo?: undefined }
		| { fieldType: "Email"; fieldTypeInfo?: undefined }
		| { fieldType: "Link to record"; fieldTypeInfo?: undefined }
		| { fieldType: "Multiline text"; fieldTypeInfo?: undefined }
		| { fieldType: "Number"; fieldTypeInfo?: undefined }
		| { fieldType: "Percent"; fieldTypeInfo?: undefined }
		| DataGridColumnDefPerson<T, TData>
		| { fieldType: "Progress bar"; fieldTypeInfo?: undefined }
		| { fieldType: "Reaction"; fieldTypeInfo?: undefined }
		| { fieldType: "Emoji reactions"; fieldTypeInfo?: undefined }
		| { fieldType: "Table"; fieldTypeInfo?: undefined }
		| DataGridColumnDefTag<T, TData>
		| { fieldType: "Text"; fieldTypeInfo?: undefined }
		| { fieldType: "Time distance to now"; fieldTypeInfo?: undefined }
		| { fieldType: "Time"; fieldTypeInfo?: undefined }
		| DataGridColumnDefURL<T, TData>
		// Catchall.
		| { fieldType: undefined; fieldTypeInfo?: undefined }
	);

export const emptyRow = (table: Table<unknown>): unknown => {
	const columns = table.getAllColumns();

	return columns.reduce((acc, column) => {
		const columnDef = column.columnDef as DataGridColumnDef<unknown, unknown>;
		acc[column.id] = getColumnDefaultValue(table, columnDef);
		return acc;
	}, {});
};

export const getColumnDefaultValue = (
	table: Table<unknown>,
	columnDef: DataGridColumnDef<unknown, unknown>
) => {
	let defaultValue: unknown = undefined;

	switch (columnDef.fieldType) {
		case "Person": {
			const desiredDefaultValue = columnDef.fieldTypeInfo?.defaultValue;
			if (!desiredDefaultValue || desiredDefaultValue === "Empty") {
				return;
			}

			defaultValue = table.options.meta?.currentUser;

			break;
		}
		case "Tag":
			defaultValue = columnDef.fieldTypeInfo?.defaultValue;
			break;
	}

	return defaultValue;
};

/**
 * A "processed" `DataGridColumnDef` that has been converted to a `ColumnDef` that can be used
 * `@tanstack/react-table` to render a column.
 */
export type ProcessedDataGridColumnDef<TData, T> = ColumnDef<TData, T> & {
	fieldType?: ColumnType;
	copyable?: boolean;
	defaultAggregationFn?: AggregationFnOption<TData>;
	setActiveTableTab: (tab: string | undefined, state: ActiveTabState) => void;
	readonly?: boolean;
};

/**
 * The set of aggregation functions that have been selected for each column in the DataGrid.
 */
export type AggregationFnState<TData> = Record<number, AggregationFnOption<TData>>;
